import {
  Accordion,
  AttributeHeader,
  AttributeValue,
  Button,
  CustomMobileAccordionType,
  DataTable,
  DataTableDataType,
  Filters,
  Header,
  Icon,
  ScrollContainer,
  Search,
  Select,
  SortFilters,
  StickyFooterButtons,
  tableSort,
  useBreakpoints,
  useLoading,
  ValueOpt,
} from "best-common-react";
import React, { useEffect, useState } from "react";
import { graphQl } from "../../../api/RequesTixApi";
import RouteConstants, { urlReplace } from "../../../constants/RouteConstants";
import { useNavigate, Link } from "react-router";
import IconLinkFormatter from "../../../components/tables/IconLinkFormatter";
type ProjectQL = {
  projectId: number;
  name: string;
  description: string;
  active: boolean;
  users: {
    employeeUserId: number;
  }[];
};

type ProjectData = DataTableDataType & {
  id: number;
  editLink: string;
  name: string;
  description: string;
  users: number;
  active: boolean;
};

const createQuery = () => `{
  getAllProjects{
    projectId,
    name,
    description,
    active,
    users {
      employeeUserId,
    }
  }
}
`;

const IsActiveFormatter = ({ value }) => value && <Icon iconName="fa fa-check" />;

const Columns = [
  {
    name: "",
    key: "editLink",
    width: 35,
    readonlyFormatter: IconLinkFormatter,
    icon: "fa-pencil-alt",
    fixed: true,
  },
  {
    name: "Name",
    key: "name",
    sortable: true,
    width: 250,
    fixed: true,
  },
  {
    name: "Users",
    key: "users",
    width: 150,
    cellClass: "justify-content-center",
    sortable: true,
  },
  {
    name: "Active",
    key: "active",
    width: 100,
    cellClass: "justify-content-center",
    readonlyFormatter: IsActiveFormatter,
  },
  {
    name: "Description",
    key: "description",
    sortable: true,
    minWidth: 250,
  },
];

const AdminProjectAccordion = ({
  data: { name, users, active, description, editLink },
  ...rest
}: CustomMobileAccordionType<ProjectData>) => (
  <Accordion {...rest}>
    <Accordion.Header>
      <Link to={editLink}>
        <Icon iconName="fa-pencil-alt" className="fa-lg bcr-me-3" />
      </Link>
      {name}
    </Accordion.Header>
    <Accordion.Body>
      <div>
        <AttributeHeader>Users</AttributeHeader>
        <AttributeValue>{users}</AttributeValue>
      </div>
      <div>
        <AttributeHeader>Active</AttributeHeader>
        <AttributeValue>
          <IsActiveFormatter value={active} />
        </AttributeValue>
      </div>
      <div>
        <AttributeHeader>Description</AttributeHeader>
        <AttributeValue>{description}</AttributeValue>
      </div>
    </Accordion.Body>
  </Accordion>
);

const filter = (projects: ProjectData[], search: string, active: boolean): ProjectData[] => {
  return projects
    .filter((p) => p.active === active)
    .filter((p) => {
      if (search && search.length) {
        return (
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          p.description.toLowerCase().includes(search.toLowerCase())
        );
      } else {
        return true;
      }
    });
};

const ActiveInactiveOpts: ValueOpt<number>[] = [
  {
    label: "All",
    value: -1,
  },
  {
    label: "Active",
    value: 1,
  },
  {
    label: "Inactive",
    value: 2,
  },
];

const AdminProject = () => {
  const navigate = useNavigate();
  const { loading, setLoadingNonBlocking } = useLoading();
  const { isMobile } = useBreakpoints();
  const [active, setActive] = useState<ValueOpt<number>>(ActiveInactiveOpts[1]);
  const [search, setSearch] = useState<string>("");
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [visibleProjects, setVisibleProjects] = useState([]);
  const [sortFilters, setSortFilters] = React.useState<SortFilters<ProjectData>>({ key: "name", direction: "ASC" });

  const createNewProject = (id: number | string) => {
    navigate(urlReplace(RouteConstants.ADMIN.PROJECTS_EDIT, { projectId: id }));
  };

  const fetchProjects = async () => {
    try {
      setLoadingNonBlocking(true);
      const queryObj = {
        query: createQuery(),
        variables: null,
        operationName: null,
      };
      const data = await graphQl(queryObj);
      if (data) {
        const projs: ProjectQL[] = data.getAllProjects;
        setProjects(
          projs.map((d: ProjectQL) => ({
            id: d.projectId,
            editLink: urlReplace(RouteConstants.ADMIN.PROJECTS_EDIT, { projectId: d.projectId }),
            name: d.name,
            description: d.description,
            users: d.users ? d.users.length : 0,
            active: d.active,
          })),
        );
      } else {
        setProjects([]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingNonBlocking(false);
    }
  };

  useEffect(() => {
    void fetchProjects();
  }, []);

  useEffect(() => {
    //@ts-ignore
    setVisibleProjects(tableSort(filter(projects, search, active.value === 1), sortFilters));
  }, [sortFilters, projects, search, active]);

  return (
    <div>
      <Header
        sticky
        title={
          <div className="d-flex justify-content-between align-items-center">
            <div>Projects</div>
            <StickyFooterButtons startsOpen={true}>
              <Button variant="default" onClick={() => createNewProject(-1)}>
                Create New Project
              </Button>
            </StickyFooterButtons>
          </div>
        }
        flexColumn
      >
        <Filters
          actionIconPosition="absolute"
          numberDisplayed={isMobile ? 1 : 2}
          indexesToDisplay={isMobile ? [1] : undefined}
        >
          <Select
            id="activeInactive"
            label="Active/Inactive"
            options={ActiveInactiveOpts}
            value={active}
            onChange={(value: ValueOpt<number>) => setActive(value)}
            placeholder="Active/Inactive"
            gutterBottom
          />
          <Search
            id="search"
            label="Search Projects"
            value={search}
            onChange={setSearch}
            placeholder="begin typing to filter projects"
            gutterBottom
          />
        </Filters>
      </Header>
      <ScrollContainer className="bcr-p-2">
        <DataTable
          className="bcr-mb-2"
          accordion={AdminProjectAccordion}
          columns={Columns}
          data={visibleProjects}
          sortColumn={sortFilters["key"]}
          sortDirection={sortFilters["direction"]}
          sortFunction={(key, direction) => setSortFilters({ key, direction })}
          statusTextOverride={(_selected, total) => `${total} Projects`}
          loading={loading}
        />
      </ScrollContainer>
    </div>
  );
};

export default AdminProject;
